import React, { ReactNode, useEffect, useState } from "react";
import * as Color from "~/Color";
import { Reference } from "~/Data";

import styled from "styled-components";

import {
  EuiFilterButton,
  EuiFilterGroup,
  EuiIcon,
  EuiPopover,
  EuiPopoverTitle,
  EuiSelectable,
} from "@equipmentshare/ds2";
import { Danger } from "~/ErrorMessage";

const FilterLabel = styled.span`
  padding-right: 0.5em;
`;

export type FilterOption = {
  readonly label: string;
  // eslint-disable-next-line functional/prefer-readonly-type
  checked?: "on" | "off";
  readonly data: Reference.Reference;
};

export type FilterProps = {
  readonly isLoading: boolean;
  // eslint-disable-next-line functional/prefer-readonly-type
  readonly initialItems: FilterOption[];
  readonly label: string;
  readonly onClose: (items: readonly FilterOption[]) => void;
  readonly desiredWidth?: string;
};

export const isChecked = (item: FilterOption) => {
  return item.checked === "on";
};

export const Filter = ({
  initialItems,
  label,
  isLoading,
  onClose,
  desiredWidth = "30vw",
}: FilterProps) => {
  // eslint-disable-next-line functional/prefer-readonly-type
  const [items, setItems] = useState<FilterOption[]>(initialItems);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  const onClick = () => {
    if (isPopoverOpen) {
      onClose(items);
    }
    setIsPopoverOpen(!isPopoverOpen);
  };

  const onClearAll = () => {
    setItems(items.map((item) => ({ ...item, checked: undefined })));
  };

  // eslint-disable-next-line functional/prefer-readonly-type
  const onChange = (items: FilterOption[]) => {
    setItems(items);
  };

  const close = () => {
    onClose(items);
    setIsPopoverOpen(false);
  };

  return (
    <FilterComponentContainer>
      <EuiFilterGroup>
        <EuiPopover
          button={
            <EuiFilterButton
              iconType="arrowDown"
              onClick={onClick}
              isSelected={isPopoverOpen}
              numFilters={items.filter((item) => !isChecked(item)).length}
              hasActiveFilters={!!items.find(isChecked)}
              numActiveFilters={
                items.filter((item) => item.checked === "on").length
              }
            >
              {label}
            </EuiFilterButton>
          }
          isOpen={isPopoverOpen}
          closePopover={close}
        >
          <EuiSelectable
            searchable
            searchProps={{
              placeholder: `Search ${label}s`,
              compressed: true,
            }}
            aria-label={`Search ${label}s`}
            options={items}
            onChange={onChange}
            isLoading={isLoading}
            loadingMessage="Loading filters"
            emptyMessage="No filters available"
            noMatchesMessage="No matches found"
          >
            {(list, search) => (
              <SelectableContentContainer width={desiredWidth}>
                <EuiPopoverTitle paddingSize="s">{search}</EuiPopoverTitle>
                {list}
                <ClearAllButton onClick={onClearAll}>
                  <EuiIcon type="crossInACircleFilled"></EuiIcon>
                  Clear all
                </ClearAllButton>
              </SelectableContentContainer>
            )}
          </EuiSelectable>
        </EuiPopover>
      </EuiFilterGroup>
    </FilterComponentContainer>
  );
};

type ContainerProps = { readonly width: string };

const SelectableContentContainer = styled.div<ContainerProps>`
  min-width: 300px;
  width: ${(props) => props.width};
`;

const ClearAllButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding-top: 1em;
  border-top: 1px solid ${Color.veryWhiteGrey};
  font-weight: 500;
  grid-gap: 1em;
  cursor: pointer;
  color: ${Danger.color()};
`;

const FilterComponentContainer = styled.div`
  padding: 0 0.5em;
`;

export type FilterRowProps = {
  readonly children: ReactNode;
};

export const FilterRow = ({ children }: FilterRowProps) => {
  return (
    <FilterRowContainer>
      <FilterLabel>Filters:</FilterLabel>
      <ChildContainer>{children}</ChildContainer>
    </FilterRowContainer>
  );
};

const FilterRowContainer = styled.div`
  padding-bottom: 1.5em;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

const ChildContainer = styled.span`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-item: center;
`;
